import useAuthToken from '../../hooks/useAuthToken';
import * as Api from '../../util/api';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IPRestrictionEntity,IPTemplateEntity } from './IPRestrictionInterface';

export function useGetIPRestriction(enterpriseId: string, storeId: string) {
  const token = useAuthToken();
  return useQuery(['getIPRestriction', enterpriseId, storeId], () => Api.getIPRestriction(token, enterpriseId, storeId),
  { enabled: !!token && !!enterpriseId && !!storeId});
}

export function useGetIPTemplate(enterpriseId: string, storeId: string) {
  const token = useAuthToken();
  return useQuery(['getIPTemplate', enterpriseId, storeId], () => Api.getIPTemplate(token, enterpriseId, storeId),
  { enabled: !!token && !!enterpriseId && !!storeId});
}

async function checkVpnStatus(url: string, timeoutMs = 3000): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(`${url}?_=${Date.now()}`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export function useUserIPRestriction(enterpriseId: string, storeId: string) {
  const token = useAuthToken();

  const query = useQuery({
      queryKey: ['getUserIPRestriction', enterpriseId, storeId],
      queryFn: async () => {
        const data = await Api.fetchUserIPRestriction(token, enterpriseId, storeId);
        const url = data?.[0]?.officeInternalUrl;
        if (!url) return data;
  
        const isConnected = await checkVpnStatus(url);
        return isConnected
          ? data?.map(item =>
              item.isRestricted === true ? { ...item, isRestricted: false } : item
            )
          : data;
      },
      enabled: !!token && !!enterpriseId && !!storeId
    });

  return { data: query.data, isLoading: query.isLoading };
}

export function useSaveIPTemplate({ enterpriseId, storeId, onSuccess, onError }: any) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (model: IPTemplateEntity) => Api.saveIPTemplate(token, enterpriseId, storeId, model),
    {
      onError: () => {
        onError();
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(['getIPTemplate']);
        queryClient.invalidateQueries(['getIPRestriction']);
      },
    }
  );

  return { mutation };
}


export const useSaveIPRestriction = ({ enterpriseId, storeId, onSuccess, onError }: any) => {
  const token = useAuthToken();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (model: IPRestrictionEntity) => Api.saveIPRestriction(token, enterpriseId, storeId, model),
    {
      onError: () => {
        onError();
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries(['getIPTemplate']);
        queryClient.invalidateQueries(['getIPRestriction']);
      },
    }
  );

  return { mutation };
};